import { create } from 'zustand';
import request from '@/utils/request';

interface AdminUser {
  id: number;
  username: string;
}

interface AuthState {
  token: string | null;
  admin: AdminUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  checkSession: () => Promise<void>;
}

let sessionRevision = 0;
let sessionCheck: { token: string; revision: number; promise: Promise<void> } | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  token: localStorage.getItem('admin_token'),
  admin: (() => {
    try {
      const stored = localStorage.getItem('admin_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })(),
  isAuthenticated: !!localStorage.getItem('admin_token'),
  loading: false,

  login: async (username: string, password: string) => {
    const revision = ++sessionRevision;
    const { data } = await request.post('/admin/login', { username, password });
    if (revision !== sessionRevision) return;
    const { token, admin } = data;
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(admin));
    set({ token, admin, isAuthenticated: true, loading: false });
  },

  logout: () => {
    sessionRevision += 1;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    set({ token: null, admin: null, isAuthenticated: false, loading: false });
  },

  checkSession: () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      set({ token: null, admin: null, isAuthenticated: false, loading: false });
      return Promise.resolve();
    }
    const revision = sessionRevision;
    if (sessionCheck?.token === token && sessionCheck.revision === revision) {
      return sessionCheck.promise;
    }
    const isCurrent = () =>
      revision === sessionRevision && localStorage.getItem('admin_token') === token;
    const promise = (async () => {
      try {
        const { data } = await request.get('/admin/session');
        if (!isCurrent()) return;
        const admin = data.admin ?? data;
        // Keep compatibility while the frontend/backend are deployed separately.
        const renewedToken = data.token ?? token;
        localStorage.setItem('admin_token', renewedToken);
        localStorage.setItem('admin_user', JSON.stringify(admin));
        set({ token: renewedToken, admin, isAuthenticated: true, loading: false });
      } catch (error) {
        if (revision !== sessionRevision) return;
        const currentToken = localStorage.getItem('admin_token');
        // The request interceptor may already have removed this exact token.
        if (currentToken !== null && currentToken !== token) return;
        if ((error as { response?: { status?: number } }).response?.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          set({ token: null, admin: null, isAuthenticated: false, loading: false });
        }
        // A timeout, offline browser, 429, or server failure is not a logout.
      }
    })().finally(() => {
      if (sessionCheck?.promise === promise) sessionCheck = null;
    });
    sessionCheck = { token, revision, promise };
    return promise;
  },
}));

// Another tab can renew the token or explicitly log out. Keep the visible auth
// state in sync, and invalidate any older session check still in flight here.
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== 'admin_token' && event.key !== 'admin_user') return;
  sessionRevision += 1;
  const token = localStorage.getItem('admin_token');
  let admin: AdminUser | null = null;
  try {
    admin = JSON.parse(localStorage.getItem('admin_user') ?? 'null');
  } catch {
    // A session check will restore the account information.
  }
  useAuthStore.setState({ token, admin, isAuthenticated: Boolean(token), loading: false });
});
